// DXF / outside-cut vendor step — used by cut_blank + hybrid parts.
//
// Captures the list of DXF files going out for laser/waterjet/plasma/etc.
// cutting. Each entry: filename + vendor + setup cost + per-piece cost.
// Storage path is `part.dxfFiles[]` (DxfFileEntry per crmTypes).
//
// Real file upload + auto-vendor-routing is a future pass; today the
// filename is a text input (operator types the .dxf name they're sending).

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { DxfFileEntry } from '@/shared/crmTypes';

const VENDOR_OPTIONS = [
  'KMF laser',
  'Pittsfield Plasma',
  'Hydrocut Waterjet',
  'Industrial Laser',
  'Other',
];

export function DxfStep({ part, onChange }: StepProps) {
  const rows = part.dxfFiles ?? [];

  function updateRow(i: number, patch: Partial<DxfFileEntry>) {
    const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange({ dxfFiles: next });
  }

  function addRow() {
    const next: DxfFileEntry[] = [
      ...rows,
      { filename: '', vendor: VENDOR_OPTIONS[0], setupCost: 0, perPieceCost: 0 },
    ];
    onChange({ dxfFiles: next });
  }

  function removeRow(i: number) {
    onChange({ dxfFiles: rows.filter((_, idx) => idx !== i) });
  }

  return (
    <div className="space-y-3">
      {rows.length === 0 && (
        <div className="text-sm text-text-mute italic border border-dashed border-line-dark rounded p-4 text-center">
          No DXF entries yet. Add one per file going out for cutting.
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} className="border border-line rounded p-3 bg-bg/30 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
            <Field label={`DXF #${i + 1} filename`} className="sm:col-span-3">
              <input
                type="text"
                value={r.filename ?? ''}
                onChange={(e) => updateRow(i, { filename: e.target.value })}
                placeholder="BRK-2245-A.dxf"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
            </Field>
            <Field label="Vendor" className="sm:col-span-2">
              <select
                value={r.vendor ?? ''}
                onChange={(e) => updateRow(i, { vendor: e.target.value })}
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
              >
                <option value="">— pick vendor —</option>
                {VENDOR_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <Field label="Setup cost ($)">
              <NumberInput
                value={r.setupCost}
                onChange={(v) => updateRow(i, { setupCost: v })}
                step={1}
                unit="$"
                placeholder="35"
                numpadTitle="Setup cost"
              />
            </Field>
            <Field label="Per-piece cost ($)">
              <NumberInput
                value={r.perPieceCost}
                onChange={(v) => updateRow(i, { perPieceCost: v })}
                step={1}
                unit="$"
                placeholder="2.50"
                numpadTitle="Per-piece cost"
              />
            </Field>
            <div className="text-right">
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-xs text-text-mute hover:text-orange py-1.5 px-2"
              >
                ✕ Remove
              </button>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addRow}
        className="w-full text-sm text-orange border border-dashed border-orange/40 rounded py-2 hover:bg-orange/5 font-semibold"
      >
        + Add DXF entry
      </button>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

function Field({ label, children, className }: FieldProps) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
